// Storage utilities - API-based for persistent data, localStorage for temporary data
import { api } from './api';

const STORAGE_KEYS = {
  SETUP_DATA: 'setup_data',
  REFLECTIONS: 'reflections',
  AUDIO_CACHE: 'audio_cache'
};

export const storage = {
  // Plans - Now using backend API
  getPlans: async () => {
    try {
      const response = await api.plans.getAll();
      // API returns { data: [...] }, extract the actual plans array
      return response?.data || response || [];
    } catch (error) {
      console.error('Error reading plans:', error);
      return [];
    }
  },

  savePlan: async (plan) => {
    try {
      let response;
      if (plan.id && plan.created_at) {
        // Existing plan - update
        response = await api.plans.update(plan.id, plan);
      } else {
        // New plan - create
        response = await api.plans.create(plan);
      }
      // API returns { data: {...} }, extract the actual plan data
      return response?.data || response;
    } catch (error) {
      console.error('Error saving plan:', error);
      throw error;
    }
  },

  getPlan: async (id) => {
    try {
      const response = await api.plans.getById(id);
      // API returns { data: {...} }, extract the actual plan data
      return response?.data || response;
    } catch (error) {
      console.error('Error reading plan:', error);
      return null;
    }
  },

  deletePlan: async (id) => {
    try {
      await api.plans.delete(id);
      return true;
    } catch (error) {
      console.error('Error deleting plan:', error);
      return false;
    }
  },

  // Setup data
  getSetupData: () => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.SETUP_DATA);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error reading setup data:', error);
      return null;
    }
  },

  saveSetupData: (data) => {
    try {
      localStorage.setItem(STORAGE_KEYS.SETUP_DATA, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Error saving setup data:', error);
      return false;
    }
  },

  // Reflections
  getReflections: (planId) => {
    try {
      const reflections = localStorage.getItem(STORAGE_KEYS.REFLECTIONS);
      const all = reflections ? JSON.parse(reflections) : [];
      return planId ? all.filter(r => r.planId === planId) : all;
    } catch (error) {
      console.error('Error reading reflections:', error);
      return [];
    }
  },

  saveReflection: (reflection) => {
    try {
      const reflections = storage.getReflections();
      reflections.push({ ...reflection, timestamp: new Date().toISOString() });
      localStorage.setItem(STORAGE_KEYS.REFLECTIONS, JSON.stringify(reflections));
      return true;
    } catch (error) {
      console.error('Error saving reflection:', error);
      return false;
    }
  },

  // Audio cache
  getCachedAudio: (key) => {
    try {
      const cache = localStorage.getItem(STORAGE_KEYS.AUDIO_CACHE);
      const allCache = cache ? JSON.parse(cache) : {};
      return allCache[key];
    } catch (error) {
      console.error('Error reading audio cache:', error);
      return null;
    }
  },

  cacheAudio: (key, audioData) => {
    try {
      const cache = localStorage.getItem(STORAGE_KEYS.AUDIO_CACHE);
      const allCache = cache ? JSON.parse(cache) : {};
      allCache[key] = audioData;
      localStorage.setItem(STORAGE_KEYS.AUDIO_CACHE, JSON.stringify(allCache));
      return true;
    } catch (error) {
      console.error('Error caching audio:', error);
      return false;
    }
  },

  clearAll: () => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
      return true;
    } catch (error) {
      console.error('Error clearing storage:', error);
      return false;
    }
  }
};
